import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CodeGenerator, PresetCharSet } from '@app/features/system-settings/tabs/serialization-settings/models/code-generator.model';

@Injectable({
  providedIn: 'root'
})
export class CodeGeneratorService {
  private generatorsSubject = new BehaviorSubject<CodeGenerator[]>([]);

  public generators$ = this.generatorsSubject.asObservable();

  public presetCharSets: { [key: string]: PresetCharSet } = {
    alphabetic_upper: {
      name: 'Alphabetic (Uppercase)',
      values: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'],
      preview: 'A, B, C, D, E, F...'
    },
    alphabetic_lower: {
      name: 'Alphabetic (Lowercase)',
      values: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'],
      preview: 'a, b, c, d, e, f...'
    },
    alphabetic_mixed: {
      name: 'Alphabetic (Mixed Case)',
      values: ['a', 'A', 'b', 'B', 'c', 'C', 'd', 'D', 'e', 'E', 'f', 'F', 'g', 'G', 'h', 'H', 'i', 'I', 'j', 'J', 'k', 'K', 'l', 'L', 'm', 'M', 'n', 'N', 'o', 'O', 'p', 'P', 'q', 'Q', 'r', 'R', 's', 'S', 't', 'T', 'u', 'U', 'v', 'V', 'w', 'W', 'x', 'X', 'y', 'Y', 'z', 'Z'],
      preview: 'a, A, b, B, c, C...'
    },
    numeric: {
      name: 'Numeric',
      values: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
      preview: '0, 1, 2, 3, 4, 5, 6, 7, 8, 9'
    },
    symbols_basic: {
      name: 'Basic Symbols',
      values: ['!', '%', '&', '*', '-', '.', '/', '_', '+'],
      preview: '!, %, &, *, -, ., /, _, +'
    },
    symbols_extended: {
      name: 'Extended Symbols',
      values: ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '_', '+', '=', '-', '[', ']', '{', '}', '|', '\\', ':', ';', '"', '\'', '<', '>', ',', '.', '?', '/', '~', '`'],
      preview: '!, @, #, $, %, ^, &, *...'
    },
    hexadecimal: {
      name: 'Hexadecimal',
      values: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F'],
      preview: '0, 1, 2, 3, 4, 5, 6, 7, 8, 9, A, B, C, D, E, F'
    }
  };

  addGenerator(generator: CodeGenerator): void {
    const currentGenerators = this.generatorsSubject.value;
    this.generatorsSubject.next([...currentGenerators, generator]);
  }

  generatePreview(generator: CodeGenerator): string {
    const { mask, maskTranslation } = generator;

    if (!mask || typeof mask !== 'string') {
      return '';
    }

    let preview = mask;

    Object.entries(maskTranslation || {}).forEach(([char, values]) => {
      if (values.length > 0) {
        const regex = new RegExp(char, 'g');
        preview = preview.replace(regex, () => {
          return values[Math.floor(Math.random() * values.length)];
        });
      }
    });

    return preview;
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard');
    }
  }
}
